import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, catchError, map, of, switchMap } from 'rxjs';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Menu } from 'primeng/menu';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';

import { DomService } from '../../core/services/dom.service';
import { IglesiasService } from '../../core/services/iglesias.service';
import { SolicitudesService } from '../../core/services/solicitudes.service';
import { UserStore } from '../../core/services/user-store.service';
import { UsersService } from '../../core/services/users.service';
import { ContentCardComponent } from '../../shared/components/content-card/content-card.component';
import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';
import {
  buildConsulFechas,
  fechaDomLocal,
  mapSemanasAsistencia,
  PerfilAsistenciaResponse,
  SemanaAsistencia,
  ultimoDomingo,
} from '../../shared/helpers/perfil-asistencia';
import {
  emptyOr,
  formatDisplayName,
  formatTextoPropio,
  formatFechaNacimiento,
  mapsHref,
  perfilAvatarStyle,
  perfilInitials,
  whatsappHref,
} from '../../shared/helpers/perfil-display';
import { UserListItem, UserProfile } from '../../shared/interfaces/user.interface';
import { EdadPipe } from '../../shared/pipes/edad.pipe';

/**
 * SCRUM-89 shell + SCRUM-90 datos/foto/faltas.
 * SCRUM-91 asistencia (5 semanas). SCRUM-92 acciones de perfil.
 */
@Component({
  selector: 'app-perfil',
  standalone: true,
  imports: [
    FormsModule,
    ContentCardComponent,
    Card,
    Button,
    Menu,
    Avatar,
    Dialog,
    Checkbox,
    Select,
    ConfirmDialog,
    RouterLink,
    EdadPipe,
    SkeletonBlockComponent,
  ],
  templateUrl: './perfil.component.html',
  styleUrl: './perfil.component.scss',
})
export class PerfilComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);
  private readonly domService = inject(DomService);
  private readonly solicitudesService = inject(SolicitudesService);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly userStore = inject(UserStore);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private loadSub?: Subscription;
  private asistenciaSub?: Subscription;
  private extraSub?: Subscription;

  readonly personaId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('personaId'))),
    { initialValue: this.route.snapshot.paramMap.get('personaId') },
  );

  readonly person = signal<UserProfile | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly semanas = signal<SemanaAsistencia[]>(mapSemanasAsistencia(ultimoDomingo(), null));
  readonly asistenciaLoading = signal(false);
  readonly asistenciaError = signal<string | null>(null);
  readonly cantCulto = signal(1);
  readonly discipulos = signal<UserListItem[]>([]);
  readonly modoTransferir = signal(false);
  readonly seleccion = signal<string[]>([]);
  readonly dialogoEnviar = signal(false);
  readonly lideresOpciones = signal<{ label: string; value: string }[]>([]);
  readonly liderDestino = signal<string | null>(null);
  readonly cargandoLideres = signal(false);
  readonly enviando = signal(false);
  readonly photoDialogOpen = signal(false);
  readonly photoPreview = signal<string | null>(null);
  private pendingPhotoFile: File | null = null;

  readonly isPersonProfile = computed(() => !!this.personaId());
  readonly isAdminViewer = computed(() => this.userStore.isAdmin());
  readonly isLiderViewer = computed(() => this.userStore.isLider());

  readonly displayName = computed(() => {
    const p = this.person();
    return p ? formatDisplayName(p) : '';
  });
  readonly fechaNacimiento = computed(() => formatFechaNacimiento(this.person()?.nacimiento));
  readonly avatarStyle = computed(() => perfilAvatarStyle(this.person()?.sexo));
  readonly initials = computed(() => {
    const p = this.person();
    return p ? perfilInitials(p) : '?';
  });
  readonly whatsappUrl = computed(() => {
    const p = this.person();
    return p ? whatsappHref(p.prefijo, p.celular, formatTextoPropio(p.nombre)) : null;
  });
  readonly mapsUrl = computed(() => {
    const p = this.person();
    return p ? mapsHref(p.direccion, p.localidad) : null;
  });
  readonly faltasLabel = computed(() => {
    const f = this.person()?.faltas;
    return f == null ? '—' : String(f);
  });
  readonly leaderLabel = computed(() => {
    const lider = this.person()?.miLider?.trim().toLowerCase();
    if (!lider || lider === 'nadie' || lider === 'sin lider' || lider === 'evadir') {
      return 'Sin lider';
    }
    return formatTextoPropio(this.person()?.miLider);
  });
  readonly localidadLabel = computed(() => formatTextoPropio(this.person()?.localidad));
  readonly ministerioLabel = computed(() => formatTextoPropio(this.person()?.ministerio));
  readonly bautismoLabel = computed(() =>
    this.person()?.bautizado ? 'Si, fue bautizado.' : 'No se bautizo.',
  );
  readonly puedeTransferir = computed(
    () => this.isAdminViewer() && !!this.person()?.lider && this.discipulos().length > 0,
  );
  readonly showReunionSelect = computed(
    () => this.cantCulto() !== 1 && (this.isAdminViewer() || this.isLiderViewer()),
  );
  readonly reunionOptions = computed(() => buildReunionOptions(this.cantCulto()));

  readonly menuItems = computed<MenuItem[]>(() => {
    const id = this.personaId();
    const admin = this.isAdminViewer();
    if (!id) {
      return [];
    }
    return [
      {
        label: 'Cambiar foto',
        icon: 'pi pi-camera',
        command: () => this.openPhotoDialog(),
      },
      {
        label: 'Editar datos',
        icon: 'pi pi-pencil',
        command: () => void this.router.navigate(['/editar-persona', id]),
      },
      {
        label: 'Reiniciar contrasena',
        icon: 'pi pi-key',
        visible: admin,
        command: () => this.confirmReiniciarPassword(id),
      },
      {
        label: 'Abrir chat',
        icon: 'pi pi-comments',
        visible: admin,
        command: () => this.confirmAbrirChat(id),
      },
    ];
  });

  constructor() {
    // Load whenever route id is present
    const id = this.personaId();
    if (id) {
      this.loadPerson(id);
    }
    this.route.paramMap.subscribe((params) => {
      const next = params.get('personaId');
      if (next) {
        this.loadPerson(next);
      } else {
        this.person.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.asistenciaSub?.unsubscribe();
    this.extraSub?.unsubscribe();
  }

  emptyOr = emptyOr;
  formatTextoPropio = formatTextoPropio;

  nombrePersona(persona: { nombre?: string | null; nombre2?: string | null; apellido?: string | null }): string {
    return formatDisplayName({
      nombre: persona.nombre ?? '',
      nombre2: persona.nombre2 ?? '',
      apellido: persona.apellido ?? '',
    });
  }

  private loadPerson(id: string): void {
    this.cancelarTransferencia();
    this.loading.set(true);
    this.loadError.set(null);
    this.loadSub?.unsubscribe();
    this.loadSub = this.usersService.getById(id).subscribe({
      next: (res) => {
        this.person.set(res.data);
        this.loading.set(false);
        this.loadAsistencia(id);
        this.loadReunionYLiderazgo(id);
      },
      error: () => {
        this.loadError.set('No se pudo cargar el perfil.');
        this.loading.set(false);
        this.person.set(null);
        this.asistenciaSub?.unsubscribe();
      },
    });
  }

  private loadAsistencia(id: string): void {
    const iglesia = this.userStore.iglesia();
    const fallback = ultimoDomingo();
    this.semanas.set(mapSemanasAsistencia(fallback, null));
    this.asistenciaError.set(null);

    if (!iglesia) {
      this.asistenciaLoading.set(false);
      this.asistenciaError.set('No hay iglesia en la sesion para consultar asistencia.');
      return;
    }

    this.asistenciaLoading.set(true);
    this.asistenciaSub?.unsubscribe();
    this.asistenciaSub = this.domService
      .actual()
      .pipe(
        map((dom) => fechaDomLocal(dom.dom) ?? fallback),
        catchError(() => of(fallback)),
        switchMap((base) => {
          this.semanas.set(mapSemanasAsistencia(base, null));
          const fechas = buildConsulFechas(base);
          return this.usersService.getPerfilAsistencia(fechas.iso, iglesia, id).pipe(
            map((data) => ({ base, data })),
          );
        }),
      )
      .subscribe({
        next: ({ base, data }: { base: Date; data: PerfilAsistenciaResponse }) => {
          this.semanas.set(mapSemanasAsistencia(base, data));
          this.asistenciaLoading.set(false);
        },
        error: () => {
          this.asistenciaLoading.set(false);
          this.asistenciaError.set('No se pudo cargar la asistencia.');
        },
      });
  }

  private loadReunionYLiderazgo(id: string): void {
    this.extraSub?.unsubscribe();
    this.iglesiasService.getCurrentIglesia().subscribe({
      next: (iglesia) => this.cantCulto.set(iglesia.cantCulto ?? 1),
      error: () => this.cantCulto.set(1),
    });

    if (!this.isAdminViewer()) {
      this.discipulos.set([]);
      return;
    }

    this.extraSub = this.usersService.listDiscipulos(id).subscribe({
      next: (personas) => this.discipulos.set(personas),
      error: () => this.discipulos.set([]),
    });
  }

  onReunionChange(value: number): void {
    const id = this.personaId();
    const current = this.person();
    if (!id || !current) {
      return;
    }
    const previous = current.reunionSelect ?? 0;
    this.person.set({ ...current, reunionSelect: value });
    this.usersService.update(id, { reunionSelect: value }).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: 'Listo', detail: 'Cambio aplicado.' });
      },
      error: () => {
        this.person.set({ ...current, reunionSelect: previous });
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar la reunion.',
        });
      },
    });
  }

  confirmReiniciarPassword(id: string): void {
    this.confirmation.confirm({
      header: 'Reiniciar contrasena',
      message: 'La contrasena quedara vacia.',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.usersService.update(id, { password: '' }).subscribe({
          next: () => {
            this.messages.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'La contraseña quedó vacía. Puede entrar dejando el campo en blanco.',
            });
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo reiniciar la contrasena.',
            });
          },
        });
      },
    });
  }

  confirmAbrirChat(id: string): void {
    this.confirmation.confirm({
      header: 'Abrir chat',
      message: 'El chat de esta persona estara disponible en Solicitudes.',
      icon: 'pi pi-comments',
      acceptLabel: 'Ok',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.solicitudesService.abrirChat(id).subscribe({
          next: () => {
            this.messages.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'Chat disponible en Solicitudes.',
            });
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo abrir el chat.',
            });
          },
        });
      },
    });
  }

  estaSeleccionado(id: string): boolean {
    return this.seleccion().includes(id);
  }

  empezarTransferencia(): void {
    if (!this.puedeTransferir()) {
      return;
    }
    this.seleccion.set([]);
    this.liderDestino.set(null);
    this.modoTransferir.set(true);
  }

  cancelarTransferencia(): void {
    this.modoTransferir.set(false);
    this.seleccion.set([]);
    this.dialogoEnviar.set(false);
    this.liderDestino.set(null);
    this.enviando.set(false);
  }

  toggleDiscipulo(id: string, marcado: boolean): void {
    const actual = this.seleccion();
    this.seleccion.set(
      marcado ? [...actual, id] : actual.filter((item) => item !== id),
    );
  }

  abrirEnviar(): void {
    if (this.seleccion().length === 0) {
      return;
    }
    this.liderDestino.set(null);
    this.dialogoEnviar.set(true);
    this.cargarLideres();
  }

  onDialogoEnviarVisible(visible: boolean): void {
    this.dialogoEnviar.set(visible);
    if (!visible) {
      this.liderDestino.set(null);
    }
  }

  aceptarTransferencia(): void {
    const origen = this.personaId();
    const destino = this.liderDestino();
    const ids = this.seleccion();
    if (!origen || !destino || ids.length === 0 || this.enviando()) {
      return;
    }

    this.enviando.set(true);
    this.usersService.transferirDiscipulos(origen, ids, destino).subscribe({
      next: (resultado) => {
        const enviados = new Set(ids);
        this.discipulos.update((lista) => lista.filter((item) => !enviados.has(item.id)));
        this.cancelarTransferencia();
        const cantidad = resultado.transferidos;
        this.messages.add({
          severity: 'success',
          summary: 'Listo',
          detail:
            cantidad === 1
              ? 'Se transfirió 1 persona.'
              : `Se transfirieron ${cantidad} personas.`,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.enviando.set(false);
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: this.mensajeError(err, 'No se pudieron transferir las personas.'),
        });
      },
    });
  }

  private cargarLideres(): void {
    const origen = this.personaId();
    this.cargandoLideres.set(true);
    this.lideresOpciones.set([]);
    this.usersService.getLideres().subscribe((lideres) => {
      if (this.personaId() !== origen) {
        return;
      }
      this.lideresOpciones.set(
        lideres
          .filter((lider) => lider.id !== origen)
          .map((lider) => ({
            label: this.nombrePersona(lider),
            value: lider.id,
          })),
      );
      this.cargandoLideres.set(false);
    });
  }

  private mensajeError(err: HttpErrorResponse, fallback: string): string {
    const message = err.error?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  openPhotoDialog(): void {
    this.photoPreview.set(null);
    this.pendingPhotoFile = null;
    this.photoDialogOpen.set(true);
  }

  onPhotoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.pendingPhotoFile = file;
    const reader = new FileReader();
    reader.onload = () => this.photoPreview.set(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  }

  /** SCRUM-102: upload real pendiente de endpoint de imagen en el back. */
  applyPhotoChange(): void {
    if (!this.pendingPhotoFile || !this.personaId()) {
      this.photoDialogOpen.set(false);
      return;
    }
    this.messages.add({
      severity: 'info',
      summary: 'Foto',
      detail: 'La subida de imagen se conecta cuando el back tenga endpoint de archivos (SCRUM-102).',
    });
    this.photoDialogOpen.set(false);
  }
}

const REUNION_ORDINAL = ['', '1ra', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va'];

function buildReunionOptions(cantCulto: number): { label: string; value: number }[] {
  const options = [{ label: 'Sin definir', value: 0 }];
  const total = Math.min(Math.max(cantCulto, 0), 8);
  for (let index = 1; index <= total; index += 1) {
    options.push({ label: `${REUNION_ORDINAL[index]} Reunion`, value: index });
  }
  options.push({ label: 'Multiples reuniones', value: 100 });
  options.push({ label: 'No asistira', value: -1 });
  return options;
}